import axios from 'axios'
import { useAuthStore, getInMemoryAccessToken } from '@/store/auth.store'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  // Serialize arrays as repeated keys (SubCompanyIds=1&SubCompanyIds=2),
  // not as SubCompanyIds[]=1 — ASP.NET only binds the repeat variant.
  paramsSerializer: { indexes: null },
})

axiosInstance.interceptors.request.use((config) => {
  const token = getInMemoryAccessToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

async function tryRefreshAccessToken(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!window.navigator.locks) {
      // Fallback for browsers that don't support navigator.locks (very old)
      resolve(false);
      return;
    }

    window.navigator.locks.request(
      'refresh_access_token',
      {
        mode: 'exclusive',
        ifAvailable: true,
      },
      async (lock) => {
        if (lock == null) {
          // Another tab is currently refreshing the token.
          window.navigator.locks.request(
            'refresh_access_token',
            { ifAvailable: false },
            () => resolve(useAuthStore.getState().isAuthenticated),
          )
          return
        }

        const { csrfToken, setAuth, clearAuth } = useAuthStore.getState()
        if (!csrfToken) {
          resolve(false)
          return
        }

        try {
          // IMPORTANT: send an empty body, not {}.
          // RefreshToken is a non-nullable string in the backend -> implicitly [Required].
          // With {}, ASP.NET binds an object with RefreshToken=null -> 400 validation.
          // Only an empty body triggers EmptyBodyBehavior.Allow -> request=null ->
          // the refresh token is read from the HttpOnly cookie (admin/portal path).
          const { data } = await axios.post(
            `${BASE_URL}/api/auth/refresh`,
            null,
            {
              withCredentials: true,
              headers: { 'X-CSRF-Token': csrfToken },
            },
          )
          setAuth(data)
          resolve(true)
        } catch {
          // Refresh failed: just reset state (like setNotAuthenticated
          // in the CustomerPortal). ProtectedRoute reacts to isAuthenticated=false
          // and navigates to /login itself — no hard full reload needed.
          clearAuth()
          resolve(false)
        }
      },
    )
  })
}

// Performs the refresh call itself. Callers must already hold the refresh lock.
// companyChange switches the company an administrator acts on; null leaves the customer portal.
async function performRefresh(companyChange?: { companyId: number | null }): Promise<boolean> {
  const { csrfToken, setAuth, clearAuth } = useAuthStore.getState()
  if (!csrfToken) return false

  const url = companyChange
    ? `${BASE_URL}/api/auth/refresh/company`
    : `${BASE_URL}/api/auth/refresh`

  try {
    // IMPORTANT: empty body, not {} — see tryRefreshAccessToken above.
    const { data } = await axios.post(
      url,
      null,
      {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
        // Omitted companyId means "leave the company", so it must not be sent as null.
        params: companyChange?.companyId != null ? { companyId: companyChange.companyId } : undefined,
      },
    )
    setAuth(data)
    return true
  } catch {
    clearAuth()
    return false
  }
}

/**
 * Forces a refresh and waits for the lock instead of giving up when it is taken (unlike the 401
 * path): the new token has to land in *this* tab.
 */
export function refreshAccessToken(): Promise<boolean> {
  return withRefreshLock(() => performRefresh())
}

/**
 * Switches the company an administrator acts on, null leaves the customer portal. Runs through the
 * refresh route because that is the only place a refresh token is validated before new tokens are
 * issued — the company lives in those tokens, not in the database.
 */
export function switchCompany(companyId: number | null): Promise<boolean> {
  return withRefreshLock(() => performRefresh({ companyId }))
}

/**
 * Runs fn while holding the refresh lock. Use it for every call that rotates the refresh
 * cookie itself (today: the administrator's company switch). Without the lock such a call can
 * race the 401 refresh — both rotate the cookie, the loser presents a stale token and the
 * backend revokes the whole session.
 */
export function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (!window.navigator.locks) {
    return fn()
  }

  return window.navigator.locks.request(
    'refresh_access_token',
    { mode: 'exclusive' },
    fn,
  ) as Promise<T>
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    // Only handle 401s that haven't already been retried
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error)
    }

    original._retry = true

    const success = await tryRefreshAccessToken()
    if (success) {
      const newToken = getInMemoryAccessToken()
      original.headers.Authorization = `Bearer ${newToken}`
      return axiosInstance(original)
    } else {
      return Promise.reject(error)
    }
  },
)
